<template>
  <div class="newsletter-block">
    <section class="newsletter-section">
      <div class="container">
        <div class="newsletter-wrapper">
          <div class="newsletter-content">
            <div class="newsletter-icon">
              <img src="/img/featureIcons/letter.png" alt="" />
            </div>
            <div class="newsletter-text">
              <h4>{{ $t("newsletter.title") }}</h4>
              <p>{{ $t("newsletter.description") }}</p>
            </div>
          </div>

          <div class="newsletter-form-wrapper">
            <button type="button" class="newsletter-btn" @click="openModal">
              <template v-if="successNotice">
                <span class="newsletter-btn__success">
                  <svg
                    class="newsletter-btn__successIcon"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>{{ $t("newsletter.success_short") }}</span>
                </span>
              </template>
              <template v-else>
                {{ $t("newsletter.submit") }}
              </template>
            </button>
          </div>
        </div>
      </div>
    </section>

    <div v-if="isModalOpen" class="newsletter-modal" @click.self="closeModal">
      <div class="newsletter-modal__card" role="dialog" aria-modal="true" aria-labelledby="newsletter-modal-title">
        <button type="button" class="newsletter-modal__close" @click="closeModal" aria-label="Close">
          &times;
        </button>
        <h4 id="newsletter-modal-title">{{ $t("newsletter.modal_title") }}</h4>

        <form @submit.prevent="subscribe" class="newsletter-modal__form" novalidate>
          <input
            v-model="email"
            type="email"
            :placeholder="$t('newsletter.email_placeholder')"
            class="newsletter-modal__input"
            @input="clearFieldError('email')"
          />
          <small v-if="validationErrors.email" class="field-error">
            {{ validationErrors.email }}
          </small>

          <div class="newsletter-modal__consentWrap">
            <label class="newsletter-modal__consent">
              <input
                v-model="consentGiven"
                type="checkbox"
                class="newsletter-modal__consent-checkbox"
                @change="clearFieldError('consent')"
              />
              <span>{{ consentText }}</span>
            </label>
            <small v-if="validationErrors.consent" class="field-error">
              {{ validationErrors.consent }}
            </small>
          </div>

          <button type="submit" class="newsletter-modal__submit" :disabled="isSubmitting">
            {{ isSubmitting ? $t("newsletter.sending") : $t("newsletter.submit") }}
          </button>
        </form>

        <div class="newsletter-modal__messages">
          <div v-if="successMessage" class="message success">
            {{ successMessage }}
          </div>
          <div v-if="errorMessage" class="message error">
            {{ errorMessage }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import i18nService from "@/services/i18n.service";
import fallbackToast from "@/services/toast.service";
import { subscribeNewsletter } from "@/services/newsletter.service";

export default {
  name: "Newsletter",

  data() {
    return {
      email: "",
      consentGiven: false,
      consentVersion: "v1",
      isSubmitting: false,
      isModalOpen: false,
      successMessage: "",
      successNotice: "",
      errorMessage: "",
      validationErrors: {
        email: "",
        consent: "",
      },
    };
  },
  computed: {
    consentText() {
      return this.$t("newsletter.consent_text");
    },
  },

  methods: {
    updateModalPageState() {
      const overflowValue = this.isModalOpen ? "hidden" : "";
      const overscrollValue = this.isModalOpen ? "none" : "";
      document.body.style.overflowY = overflowValue;
      document.body.style.overflow = overflowValue;
      document.body.style.overscrollBehaviorY = overscrollValue;
      document.documentElement.style.overflowY = overflowValue;
      document.documentElement.style.overflow = overflowValue;
      document.documentElement.style.overscrollBehaviorY = overscrollValue;
    },
    handleKeydown(event) {
      if (event.key === "Escape" && this.isModalOpen) {
        this.closeModal();
      }
    },
    openModal() {
      this.isModalOpen = true;
      this.successMessage = "";
      this.errorMessage = "";
      this.validationErrors = { email: "", consent: "" };
      this.updateModalPageState();
    },
    closeModal(force = false) {
      if (this.isSubmitting && !force) return;
      this.isModalOpen = false;
      this.updateModalPageState();
    },
    getValidationMessage(data) {
      const detail = data?.detail;

      if (Array.isArray(detail) && detail.length > 0) {
        const firstError = detail[0];
        if (typeof firstError === "string") {
          return firstError;
        }
        if (typeof firstError?.msg === "string" && firstError.msg.trim()) {
          return firstError.msg;
        }
      }

      if (typeof detail === "string" && detail.trim()) {
        return detail;
      }

      return this.$t("newsletter.errors.validation");
    },
    clearFieldError(field) {
      if (!field || !this.validationErrors[field]) return;
      this.validationErrors = { ...this.validationErrors, [field]: "" };
    },
    validateForm() {
      const errors = { email: "", consent: "" };
      const emailValue = String(this.email || "").trim();

      if (!emailValue) {
        errors.email = this.$t("newsletter.errors.email_required");
      }

      if (!this.consentGiven) {
        errors.consent = this.$t("newsletter.errors.consent_required");
      }

      this.validationErrors = errors;
      return !errors.email && !errors.consent;
    },
    showToast(type, message) {
      if (this.$toast && typeof this.$toast[type] === "function") {
        this.$toast[type](message);
        return;
      }

      if (fallbackToast && typeof fallbackToast[type] === "function") {
        fallbackToast[type](message);
      }
    },
    async subscribe() {
      this.isSubmitting = true;
      this.successMessage = "";
      this.errorMessage = "";
      this.successNotice = "";

      if (!this.validateForm()) {
        this.isSubmitting = false;
        return;
      }

      try {
        const payload = {
          email: this.email,
          consent_given: true,
          consent_text: this.consentText,
          consent_version: this.consentVersion,
          source_page: typeof window !== "undefined" ? window.location.href : "",
          locale: i18nService.getActiveLanguage(),
        };

        await subscribeNewsletter(payload);
        const message = this.$t("newsletter.success_localized");

        this.successMessage = message;
        this.successNotice = message;
        this.showToast("success", message);
        this.email = "";
        this.consentGiven = false;
        this.validationErrors = { email: "", consent: "" };
        this.closeModal(true);
      } catch (error) {
        const status = error?.response?.status;
        let errorMessage = this.$t("newsletter.errors.generic");

        if (status === 409) {
          errorMessage = this.$t("newsletter.errors.unable_to_complete");
        } else if (status === 422) {
          errorMessage = this.getValidationMessage(error?.response?.data);
        } else if (status === 429) {
          errorMessage = this.$t("newsletter.errors.rate_limited");
        }

        this.errorMessage = errorMessage;
        this.showToast("error", errorMessage);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
  mounted() {
    document.addEventListener("keydown", this.handleKeydown);
  },
  beforeDestroy() {
    this.isModalOpen = false;
    this.updateModalPageState();
    document.removeEventListener("keydown", this.handleKeydown);
  },
};
</script>

<style lang="scss" scoped>
$blue-1: #3554d1;
$dark-2: #0b1a3e;

.newsletter-section {
  background-color: $dark-2;
  padding: 40px 0;
}

.newsletter-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
}

// ========== LEFT SIDE ==========
.newsletter-content {
  display: flex;
  align-items: center;
  gap: 25px;
}

.newsletter-icon {
  flex-shrink: 0;
  width: 50px;
  svg {
    display: block;
  }
}

.newsletter-text {
  h4 {
    font-size: 22px;
    font-weight: 600;
    color: #fff;
    margin: 0 0 5px 0;
  }

  p {
    font-size: 15px;
    color: rgba(255, 255, 255, 0.7);
    margin: 0;
  }
}

// ========== RIGHT SIDE ==========
.newsletter-form-wrapper {
  flex-shrink: 0;
}

.newsletter-btn {
  height: 54px;
  padding: 0 30px;
  background-color: $blue-1;
  color: #fff;
  border: none;
  border-radius: 0 4px 4px 0;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s ease;
  white-space: nowrap;

  &:hover {
    background-color: darken($blue-1, 10%);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

.newsletter-btn__success {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #ffffff;
}

.newsletter-btn__successIcon {
  color: #2ecc71;
}

.newsletter-modal {
  position: fixed;
  inset: 0;
  background: rgba(5, 16, 54, 0.65);
  z-index: 2147483646;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.newsletter-modal__card {
  width: 100%;
  max-width: 520px;
  background: #fff;
  border-radius: 14px;
  padding: 28px 24px 20px;
  position: relative;
  box-shadow: 0 20px 50px rgba(5, 16, 54, 0.25);

  h4 {
    font-size: 26px;
    line-height: 1.25;
    margin: 0 0 18px;
    color: #051036;
  }
}

.newsletter-modal__close {
  position: absolute;
  top: 10px;
  right: 12px;
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 50%;
  background: #f2f5fb;
  font-size: 24px;
  line-height: 1;
  color: #4b587c;
  cursor: pointer;
}

.newsletter-modal__form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.newsletter-modal__input {
  width: 100%;
  height: 52px;
  border: 1px solid #d7ddea;
  border-radius: 10px;
  padding: 0 14px;
  font-size: 16px;
  outline: none;

  &:focus {
    border-color: $blue-1;
  }
}

.newsletter-modal__consent {
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  color: #4a5670;
  font-size: 14px;
  line-height: 1.45;

  span {
    display: block;
    margin: 0;
    max-width: 100%;
    overflow-wrap: anywhere;
  }
}

.newsletter-modal__consentWrap {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.newsletter-modal__consent-checkbox {
  width: 22px !important;
  min-width: 22px;
  max-width: 22px;
  height: 22px !important;
  margin: 0 !important;
  padding: 0 !important;
  cursor: pointer;
  justify-self: start;
  align-self: center;
  flex: 0 0 22px;
}

.newsletter-modal__submit {
  height: 50px;
  border: none;
  border-radius: 10px;
  background: $blue-1;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

.newsletter-modal__messages {
  margin-top: 10px;
}

// ========== MESSAGES ==========
.message {
  margin-top: 10px;
  font-size: 14px;

  &.success {
    color: #2ecc71;
  }

  &.error {
    color: #e74c3c;
  }
}

// ========== RESPONSIVE ==========
@media (max-width: 991px) {
  .newsletter-wrapper {
    flex-direction: column;
    text-align: center;
    gap: 30px;
  }

  .newsletter-content {
    flex-direction: column;
    gap: 15px;
  }

  .newsletter-input {
    width: 250px;
  }
}

@media (max-width: 575px) {
  .newsletter-btn {
    border-radius: 4px;
  }

  .newsletter-text h4 {
    font-size: 20px;
  }

  .newsletter-modal__card {
    padding: 24px 16px 16px;

    h4 {
      font-size: 22px;
    }
  }
}

.field-error {
  color: #c0392b;
  font-size: 12px;
  display: block;
  margin: 0;
  text-align: left;
}
</style>
